import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Card = styled(Link)`
  display: block;
  text-decoration: none;
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all var(--transition-base);
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
    border-color: var(--neutral-300);
    text-decoration: none;
  }
`;

const CardImage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: var(--neutral-200);
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform var(--transition-slow);
  }

  ${Card}:hover & img {
    transform: scale(1.05);
  }
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: var(--space-3);
  left: var(--space-3);
  background: var(--primary-700);
  color: white;
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CardContent = styled.div`
  padding: var(--space-6);
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
  font-size: var(--text-sm);
  color: var(--neutral-600);
`;

const ReadingTime = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-1);

  &::before {
    content: '📖';
    font-size: var(--text-base);
  }
`;

const CardTitle = styled.h3`
  font-size: var(--text-xl);
  line-height: 1.4;
  color: var(--neutral-900);
  margin-bottom: var(--space-3);
  font-family: var(--font-serif);
  font-weight: 700;
`;

const CardExcerpt = styled.p`
  font-size: var(--text-base);
  line-height: 1.6;
  color: var(--neutral-700);
  margin-bottom: var(--space-4);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const AuthorName = styled.span`
  font-size: var(--text-sm);
  color: var(--neutral-700);
  font-weight: 500;
`;

const Tags = styled.div`
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
  margin-top: var(--space-3);
`;

const Tag = styled.span`
  background: var(--neutral-200);
  color: var(--neutral-700);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  font-weight: 500;
`;

const PublishDate = styled.span`
  font-size: var(--text-sm);
  color: var(--neutral-600);
`;

const ArticleCard = ({ article }) => {
  const excerpt = article.content && article.content[0] 
    ? article.content[0].body.substring(0, 150) + '...'
    : 'Click to read more...';

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Card to={`/article/${article.id}`}>
      <CardImage>
        <img src={article.image} alt={article.title} />
        <CategoryBadge>{article.category}</CategoryBadge>
      </CardImage>
      <CardContent>
        <CardMeta>
          <ReadingTime>{article.readingTime}</ReadingTime>
        </CardMeta>
        <CardTitle>{article.title}</CardTitle>
        <CardExcerpt>{excerpt}</CardExcerpt>
        <CardFooter>
          <AuthorInfo>
            <AuthorName>By {article.authorName}</AuthorName>
          </AuthorInfo>
          <PublishDate>{formatDate(article.publishDate)}</PublishDate>
        </CardFooter>
        <Tags>
          {article.tags && article.tags.slice(0, 3).map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </Tags>
      </CardContent>
    </Card>
  );
};

export default ArticleCard;